import { apiRequest } from './apiClient';
import {
  CertificadoPiloto,
  EmpresaPiloto,
  LeitoPiloto,
  PerfilAssistencialPiloto,
  CompatibilidadePerfilPiloto,
  PaginatedResponse,
  SaldoCreditoPiloto,
  ValidacaoPiloto,
} from '../types/piloto';

function toQuery(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      query.set(key, String(value));
    }
  });

  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
}

export function listClientes(accessToken: string): Promise<PaginatedResponse<EmpresaPiloto>> {
  return apiRequest<PaginatedResponse<EmpresaPiloto>>(
    '/empresas?pageSize=100&papel=cliente_contratante',
    accessToken
  );
}

export function listEmpresasPorPapel(
  accessToken: string,
  papel: string
): Promise<PaginatedResponse<EmpresaPiloto>> {
  return apiRequest<PaginatedResponse<EmpresaPiloto>>(
    `/empresas${toQuery({ pageSize: 100, papel, status: 'ativo' })}`,
    accessToken
  );
}

export function getSaldoCredito(
  accessToken: string,
  clienteEmpresaId: string
): Promise<SaldoCreditoPiloto> {
  return apiRequest<SaldoCreditoPiloto>(
    `/creditos/saldo${toQuery({ clienteEmpresaId })}`,
    accessToken
  );
}

export function listLeitos(
  accessToken: string,
  clienteEmpresaId: string
): Promise<PaginatedResponse<LeitoPiloto>> {
  return apiRequest<PaginatedResponse<LeitoPiloto>>(
    `/leitos${toQuery({ clienteEmpresaId, pageSize: 50 })}`,
    accessToken
  );
}

export function criarLeito(
  accessToken: string,
  payload: {
    clienteEmpresaId: string;
    codigoLeito: string;
    identificadorInternoCliente?: string;
    municipio: string;
    uf: string;
    regiao?: string;
    perfilAssistencial: string;
  }
): Promise<LeitoPiloto> {
  return apiRequest<LeitoPiloto>('/leitos', accessToken, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function listarPerfisAssistenciais(
  accessToken: string
): Promise<{ items: PerfilAssistencialPiloto[] }> {
  return apiRequest<{ items: PerfilAssistencialPiloto[] }>(
    '/leitos/perfis-assistenciais',
    accessToken
  );
}

export function getCompatibilidadePerfil(
  accessToken: string,
  leitoId: string,
  competencia?: string
): Promise<CompatibilidadePerfilPiloto> {
  return apiRequest<CompatibilidadePerfilPiloto>(
    `/leitos/${leitoId}/compatibilidade-perfil${toQuery({ competencia })}`,
    accessToken
  );
}

export function solicitarAtivacao(
  accessToken: string,
  leitoId: string
): Promise<{ leito: LeitoPiloto; reserva: unknown; validacao: ValidacaoPiloto }> {
  return apiRequest<{ leito: LeitoPiloto; reserva: unknown; validacao: ValidacaoPiloto }>(
    `/leitos/${leitoId}/ativar`,
    accessToken,
    {
      method: 'POST',
      body: JSON.stringify({
        referencia: 'Solicitação aberta pela interface do piloto',
      }),
    }
  );
}

export function salvarCadeiaMinima(
  accessToken: string,
  leitoId: string,
  fornecedores: Array<{ empresaId: string; papel: string; ordem: number }>
): Promise<unknown> {
  return apiRequest<unknown>(`/leitos/${leitoId}/cadeia`, accessToken, {
    method: 'PUT',
    body: JSON.stringify({
      fornecedores,
    }),
  });
}

export function listValidacoes(
  accessToken: string,
  leitoId: string
): Promise<PaginatedResponse<ValidacaoPiloto>> {
  return apiRequest<PaginatedResponse<ValidacaoPiloto>>(
    `/validacoes-cadeia${toQuery({ leitoId, pageSize: 50 })}`,
    accessToken
  );
}

export function criarValidacaoMensal(
  accessToken: string,
  leitoId: string,
  competencia: string
): Promise<ValidacaoPiloto> {
  return apiRequest<ValidacaoPiloto>('/validacoes-cadeia', accessToken, {
    method: 'POST',
    body: JSON.stringify({
      leitoId,
      tipo: 'mensal',
      competencia,
      motivoAbertura: 'emissao_certificado_interface_piloto',
    }),
  });
}

export function listCertificados(
  accessToken: string,
  clienteEmpresaId: string
): Promise<PaginatedResponse<CertificadoPiloto>> {
  return apiRequest<PaginatedResponse<CertificadoPiloto>>(
    `/certificados-mensais${toQuery({ clienteEmpresaId, pageSize: 50 })}`,
    accessToken
  );
}

export function emitirCertificado(
  accessToken: string,
  leitoId: string,
  competencia: string
): Promise<CertificadoPiloto> {
  return apiRequest<CertificadoPiloto>('/certificados-mensais', accessToken, {
    method: 'POST',
    body: JSON.stringify({ leitoId, competencia }),
  });
}

export function getCertificado(
  accessToken: string,
  certificadoId: string
): Promise<CertificadoPiloto> {
  return apiRequest<CertificadoPiloto>(
    `/certificados-mensais/${certificadoId}`,
    accessToken
  );
}

export async function downloadCertificadoPdf(
  accessToken: string,
  certificadoId: string
): Promise<void> {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000/api/v1';
  const response = await fetch(`${baseUrl}/certificados-mensais/${certificadoId}/download`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      code: 'CERTIFICADO_DOWNLOAD_ERRO',
      message: 'Não foi possível baixar o certificado.',
    }));
    throw new Error(`${error.code}: ${error.message}`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `certificado-${certificadoId}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
